// src/components/CurationTab.jsx
// My Place hub — Search + Add from Photos + Places/Trips/Lists/Best
import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { useUser, API_BASE } from "../context/UserContext";
import CuratedLists from "./CuratedLists";
import BestPickerModal from "./BestPickerModal";
import PhotoPlaceImport from "./PhotoPlaceImport";
import { mapGoogleCategory } from "../constants";
import SavePlaceModal from "./SavePlaceModal";
import GoogleMapsImportModal from "./GoogleMapsImportModal";
import { STATUS_COLOR, BEST_CATEGORIES, FRONTEND_URL } from "../constants";

const FH = "'Noto Serif', Georgia, serif";
const FL = "'Manrope', -apple-system, sans-serif";
const C = {
  primary:          "#655d54",
  primaryDim:       "#595149",
  primaryContainer: "#ede0d5",
  bg:               "#faf9f6",
  surfaceLow:       "#f4f4f0",
  surfaceLowest:    "#ffffff",
  container:        "#edeeea",
  onSurface:        "#2f3430",
  onSurfaceVariant: "#5c605c",
  outlineVariant:   "#8a8e8a",
  error:            "#9e422c",
};

const isMobile = () => window.innerWidth <= 768;

const SUGGESTIONS = ["Blue Bottle Coffee SF", "Tartine Bakery", "Chez Panisse", "https://maps.app.goo.gl/..."];

export default function CurationTab({
  personalPlaces = [],
  onPlaceClick,
  onPlaceAdded,
  onDataChange,
  onViewMap,
  onViewTrip,
  onViewOnMap,
  onGuestSave,
  myBestPicks = {},
  onBestPickAdd,
  onBestPickRemove,
  onRefresh,
  pushNav,
  tutorialStep,
  onTutorialAdvance,
}) {
  const { user } = useUser();
  const { t } = useTranslation();
  const mobile = isMobile();
  const [subTab, setSubTab] = useState(() => {
    return localStorage.getItem("curation_subTab") || "trips";
  }); // "places" | "trips" | "lists" | "best"
  useEffect(() => { localStorage.setItem("curation_subTab", subTab); }, [subTab]);
  const [myFolders, setMyFolders] = useState([]);

  // Search state
  const savedPlaceIds = new Set(personalPlaces.map((p) => p.google_place_id).filter(Boolean));
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searchError, setSearchError] = useState("");
  const [pendingPlace, setPendingPlace] = useState(null);
  const [savedMsg, setSavedMsg] = useState("");
  const [showImport, setShowImport] = useState(false);
  const [importUrl, setImportUrl] = useState("");
  const [showPhotoImport, setShowPhotoImport] = useState(false);

  // Search helpers
  const isGoogleMapsLink = (text) => {
    const t = text.trim();
    return t.includes("maps.app.goo.gl/") || t.includes("google.com/maps") || t.includes("goo.gl/maps");
  };

  const searchGooglePlaces = async (q) => {
    const { Place } = await window.google.maps.importLibrary("places");
    const request = {
      textQuery: q,
      fields: ["displayName", "formattedAddress", "location", "id", "types"],
      maxResultCount: 8,
    };
    const { places } = await Place.searchByText(request);
    return places.map((p) => ({
      name: p.displayName,
      address: p.formattedAddress,
      lat: p.location.lat(),
      lng: p.location.lng(),
      google_place_id: p.id,
      category: mapGoogleCategory(p.types),
      place_url: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(p.displayName)}&query_place_id=${p.id}`,
    }));
  };

  const handleSearch = async (q = query) => {
    if (!q.trim()) return;
    if (isGoogleMapsLink(q)) {
      setImportUrl(q.trim());
      setShowImport(true);
      return;
    }
    setSearchError(""); setSearchResults([]);
    setSearching(true);
    try {
      const places = await searchGooglePlaces(q.trim());
      if (places.length > 0) setSearchResults(places);
      else setSearchError(t("search.no_places"));
    } catch (e) {
      setSearchError(t("search.search_failed"));
    } finally {
      setSearching(false);
      if (tutorialStep === 2) onTutorialAdvance?.(3);
    }
  };

  const handleSavePlace = async (place) => {
    const payload = {
      name: place.name, address: place.address, lat: place.lat, lng: place.lng,
      category: place.category, google_place_id: place.google_place_id || null,
      folder_id: place.folder_id || null, status: place.status || "visited",
      rating: place.rating || null, memo: place.memo || null,
      photo_url: place.photo_url || null,
      photo_urls: place.photo_urls || null,
      instagram_post_url: place.instagram_post_url || null,
    };
    if (user) {
      const res = await axios.post(`${API_BASE}/personal-places/`, payload);
      if (onPlaceAdded) onPlaceAdded(res.data);
    } else {
      const gp = onGuestSave?.(payload);
      if (onPlaceAdded) onPlaceAdded(gp);
    }
    setSavedMsg(`'${place.name}' ${t("search.saved_toast")}`);
    setSearchResults([]); setQuery("");
    if (tutorialStep === 4) onTutorialAdvance?.(5);
    setTimeout(() => setSavedMsg(""), 3000);
  };

  // Trips state
  const [savedTrips, setSavedTrips] = useState([]);
  const [tripsLoading, setTripsLoading] = useState(false);
  const [deletingTripId, setDeletingTripId] = useState(null);
  const [editingTrip, setEditingTrip] = useState(null); // full trip object for editing

  useEffect(() => {
    if (!user) return;
    axios.get(`${API_BASE}/folders/`)
      .then((res) => setMyFolders(res.data)).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (!user) return;
    setTripsLoading(true);
    axios.get(`${API_BASE}/trips/`)
      .then((res) => setSavedTrips(res.data))
      .catch(() => {})
      .finally(() => setTripsLoading(false));
  }, [user]);

  const handleDeleteTrip = async (tripId) => {
    if (!window.confirm(t("myplace.delete_trip_confirm"))) return;
    setDeletingTripId(tripId);
    try {
      await axios.delete(`${API_BASE}/trips/${tripId}`);
      setSavedTrips((prev) => prev.filter((t) => t.id !== tripId));
    } catch {}
    setDeletingTripId(null);
  };

  const [shareToast, setShareToast] = useState(null);

  const handleShareTrip = (tripId) => {
    const url = `${FRONTEND_URL}/trip/${tripId}`;
    navigator.clipboard?.writeText(url);
    setShareToast(t("myplace.link_copied"));
    setTimeout(() => setShareToast(null), 2000);
  };

  const handleEditTrip = async (tripId) => {
    try {
      const res = await axios.get(`${API_BASE}/trips/${tripId}`);
      setEditingTrip(res.data);
    } catch {}
  };

  const handleSaveEdit = async (updated) => {
    try {
      const stopsPayload = [];
      for (let i = 0; i < updated.stops.length; i++) {
        const s = updated.stops[i];
        // Collect existing photo URLs (server URLs)
        const existingUrls = (s.photos || []).filter((p) => p.type === "existing").map((p) => p.url);
        // Upload new photos (blob URLs or files)
        let newUrls = [];
        const newPhotos = (s.newPhotos || []).filter((p) => p.file || p.url);
        if (newPhotos.length > 0) {
          try {
            const formData = new FormData();
            for (const p of newPhotos.slice(0, 5)) {
              if (p.file) {
                formData.append("files", p.file);
              } else if (p.url && p.url.startsWith("blob:")) {
                const resp = await fetch(p.url);
                const blob = await resp.blob();
                formData.append("files", blob, `photo_${i}.jpg`);
              }
            }
            if (formData.has("files")) {
              const uploadRes = await axios.post(`${API_BASE}/upload/photos`, formData);
              newUrls = uploadRes.data.urls || [];
            }
          } catch (err) { console.error("Photo upload failed:", err); }
        }
        const allUrls = [...existingUrls, ...newUrls];
        stopsPayload.push({
          place_id: s.place_id || null,
          order: i,
          visited_at: s.visited_at || null,
          name: s.name || null,
          lat: s.lat,
          lng: s.lng,
          photo_urls: allUrls.length > 0 ? allUrls : null,
          memo: s.memo || null,
        });
      }
      const payload = {
        name: updated.name,
        date: updated.date || null,
        is_public: updated.is_public != null ? updated.is_public : undefined,
        stops: stopsPayload,
      };
      await axios.patch(`${API_BASE}/trips/${updated.id}`, payload);
      const res = await axios.get(`${API_BASE}/trips/`);
      setSavedTrips(res.data);
    } catch (err) { console.error("Trip save failed:", err); }
  };

  const handleViewSavedTrip = async (tripId) => {
    try {
      const res = await axios.get(`${API_BASE}/trips/${tripId}`);
      const trip = res.data;
      const tripData = {
        id: trip.id,
        name: trip.name,
        date: trip.date,
        stops: trip.stops.map((s) => ({
          lat: s.lat,
          lng: s.lng,
          name: s.name || "",
          time: s.visited_at || "",
          memo: s.memo || "",
          thumbnailUrl: s.photo_url || (s.photo_urls?.[0]) || null,
          photos: (s.photo_urls || []).map((url) => ({ url })),
        })),
      };
      onViewTrip?.(tripData);
    } catch {}
  };

  return (
    <div style={{ background: C.bg, minHeight: "100%", display: "flex", flexDirection: "column", paddingBottom: mobile ? 80 : 48 }}>
      {/* ── Top: Segment toggle ── */}
      <div style={{
        padding: "14px 16px 0",
        background: C.bg,
        position: "sticky", top: 0, zIndex: 5,
      }}>
        {/* Segment toggle */}
        <div style={{
          display: "flex", gap: 0, width: "100%",
          background: "rgba(237,238,234,0.6)",
          borderRadius: 10, padding: 3,
        }}>
          {[
            { key: "trips", label: t("myplace.tab_trips"), icon: "trip_origin" },
            { key: "places", label: t("myplace.tab_places"), icon: "location_on" },
            { key: "lists", label: t("myplace.tab_lists"), icon: "format_list_bulleted" },
            { key: "best", label: t("myplace.tab_best"), icon: "star" },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => { setSubTab(tab.key); setSearchResults([]); setSearchError(""); }}
              style={{
                flex: 1, padding: "8px 0",
                background: subTab === tab.key ? "#ffffff" : "transparent",
                border: "none", borderRadius: 8,
                fontFamily: FL, fontSize: 12, fontWeight: subTab === tab.key ? 700 : 500,
                color: subTab === tab.key ? C.primary : C.outlineVariant,
                cursor: "pointer",
                transition: "all 0.2s",
                boxShadow: subTab === tab.key ? "0 1px 4px rgba(47,52,48,0.08)" : "none",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 4,
              }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: 14 }}>{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div style={{ flex: 1, padding: "12px 16px" }}>
        {subTab === "places" && (
          <>
            {/* Search bar */}
            <div>
              <div style={{ position: "relative", marginBottom: 10 }}>
                <div style={{
                  position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)",
                  pointerEvents: "none",
                }}>
                  <span className="material-symbols-outlined" style={{ fontSize: 14, color: searching ? C.primary : "#a8a29e", transition: "color 0.2s" }}>search</span>
                </div>
                <input
                  data-tutorial="search-input"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  placeholder={t("search.place_hint")}
                  style={{
                    width: "100%", padding: "10px 12px 10px 32px",
                    background: C.surfaceLow, border: "none",
                    borderRadius: 8, outline: "none",
                    fontFamily: FL, color: C.onSurface,
                    boxSizing: "border-box", transition: "background 0.2s",
                  }}
                  ref={(el) => { if (el) el.style.setProperty("font-size", "14px", "important"); }}
                  onFocus={(e) => e.target.style.background = C.container}
                  onBlur={(e) => e.target.style.background = C.surfaceLow}
                />
                <div style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)" }}>
                  {searching ? (
                    <span style={{ fontFamily: FL, fontSize: 12, color: "#a8a29e" }}>{t("search.searching")}</span>
                  ) : query.trim() ? (
                    <span className="material-symbols-outlined" style={{ fontSize: 14, color: C.primary, cursor: "pointer" }}
                      onClick={() => handleSearch()}>arrow_forward</span>
                  ) : null}
                </div>
              </div>

              {/* Suggested tags */}
              {searchResults.length === 0 && !searchError && (
                <div style={{ marginBottom: 12, display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6 }}>
                  <span style={{ fontFamily: FL, fontSize: 12, textTransform: "uppercase", letterSpacing: "0.15em", color: "#a8a29e", marginRight: 2 }}>
                    {t("search.suggested")}
                  </span>
                  {SUGGESTIONS.map((s) => (
                    <button key={s} onClick={() => { setQuery(s); handleSearch(s); }}
                      style={{
                        padding: "4px 10px",
                        borderRadius: 999, border: "none",
                        background: C.surfaceLow,
                        fontFamily: FL, fontSize: 12, fontWeight: 600,
                        textTransform: "uppercase", letterSpacing: "0.1em",
                        color: "#78716c", cursor: "pointer", transition: "all 0.15s",
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = C.primaryContainer; e.currentTarget.style.color = C.primary; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = C.surfaceLow; e.currentTarget.style.color = "#78716c"; }}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}

              {/* Search results / messages */}
              {savedMsg && (
                <div style={{
                  padding: "10px 14px", background: C.surfaceLowest,
                  borderLeft: `3px solid ${C.primary}`,
                  borderRadius: 6, marginBottom: 12,
                  fontFamily: FH, fontStyle: "italic", fontSize: 14, color: C.onSurface,
                }}>
                  ✓ {savedMsg}
                </div>
              )}
              {searchError && (
                <div style={{
                  padding: "10px 14px", background: "#fef0ec",
                  borderLeft: `3px solid ${C.error}`,
                  borderRadius: 6, marginBottom: 12,
                  fontFamily: FH, fontStyle: "italic", fontSize: 14, color: C.error,
                }}>
                  {searchError}
                </div>
              )}
              {searchResults.length > 0 && (
                <section style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0 4px" }}>
                    <span style={{
                      fontFamily: FL, fontSize: 12, fontWeight: 600,
                      textTransform: "uppercase", letterSpacing: "0.2em",
                      color: C.primary,
                    }}>
                      {t("search.places_found")}
                    </span>
                    <button onClick={() => { setSearchResults([]); setQuery(""); }} style={{
                      background: "none", border: "none", cursor: "pointer",
                      fontFamily: FL, fontSize: 12, color: C.outlineVariant,
                    }}>
                      {t("myplace.clear")}
                    </button>
                  </div>
                  {searchResults.map((place, idx) => (
                    <div
                      key={place.google_place_id || idx}
                      style={{
                        background: C.surfaceLowest, borderRadius: 10,
                        overflow: "hidden", transition: "background 0.3s",
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = C.bg}
                      onMouseLeave={(e) => e.currentTarget.style.background = C.surfaceLowest}
                    >
                      <div
                        onClick={() => { if (place.lat && place.lng) onViewOnMap?.(place); }}
                        style={{ padding: "12px", cursor: place.lat ? "pointer" : "default" }}
                      >
                        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                          <div>
                            {place.category && (
                              <p style={{ margin: "0 0 6px", fontFamily: FL, fontSize: 14, textTransform: "uppercase", letterSpacing: "0.15em", color: "#a8a29e" }}>
                                {place.category}
                              </p>
                            )}
                            <h3 style={{
                              margin: "0 0 4px", fontFamily: FH,
                              fontSize: idx === 0 ? (mobile ? 20 : 17) : (mobile ? 17 : 14),
                              fontWeight: 400, color: C.onSurface, letterSpacing: "-0.01em",
                            }}>
                              {place.name}
                            </h3>
                            {place.address && (
                              <p style={{
                                margin: "0 0 8px", fontFamily: FH, fontStyle: "italic",
                                fontSize: 14, color: "#78716c",
                                display: "flex", alignItems: "center", gap: 4,
                              }}>
                                <span className="material-symbols-outlined" style={{ fontSize: 14, flexShrink: 0 }}>location_on</span>
                                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{place.address}</span>
                              </p>
                            )}
                            <div style={{ display: "flex", gap: 6 }}>
                              {place.lat && place.lng && place.google_place_id && (
                                <a
                                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(place.name)}&query_place_id=${place.google_place_id}`}
                                  target="_blank" rel="noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  style={{
                                    padding: "5px 12px", background: C.container,
                                    borderRadius: 4, fontFamily: FL, fontSize: 12,
                                    fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.1em",
                                    color: "#78716c", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4,
                                  }}>
                                  <span className="material-symbols-outlined" style={{ fontSize: 12 }}>open_in_new</span>
                                  Google Maps
                                </a>
                              )}
                            </div>
                          </div>

                          {place.google_place_id && savedPlaceIds.has(place.google_place_id) ? (
                            <span style={{
                              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                              padding: "10px 18px",
                              background: "none",
                              border: "1px solid rgba(101,93,84,0.15)",
                              borderRadius: 8,
                              fontFamily: FL, fontSize: 14, fontWeight: 600,
                              color: C.outlineVariant,
                              width: "100%", boxSizing: "border-box",
                            }}>
                              <span className="material-symbols-outlined" style={{ fontSize: 12 }}>check_circle</span>
                              {t("search.saved")}
                            </span>
                          ) : (
                            <button
                              {...(idx === 0 ? { "data-tutorial": "add-place-btn" } : {})}
                              onClick={(e) => {
                                e.stopPropagation();
                                setPendingPlace(place);
                                if (tutorialStep === 3) onTutorialAdvance?.(4);
                              }}
                              style={{
                                display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                                padding: "9px 16px",
                                background: C.primary, color: "#fff6ef",
                                border: "none", borderRadius: 7,
                                fontFamily: FL, fontSize: 14, fontWeight: 700,
                                letterSpacing: "0.04em",
                                cursor: "pointer", transition: "background 0.15s",
                                width: "100%",
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.background = C.primaryDim}
                              onMouseLeave={(e) => e.currentTarget.style.background = C.primary}
                            >
                              <span className="material-symbols-outlined" style={{ fontSize: 16 }}>add</span>
                              {t("search.add_to_my_places")}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </section>
              )}
            </div>

            <PlacesSection
              personalPlaces={personalPlaces}
              myFolders={myFolders}
              onPlaceClick={onPlaceClick}
              onViewMap={onViewMap}
              mobile={mobile}
            />
          </>
        )}

        {subTab === "trips" && (
          <>
            {/* Add from Photos */}
            <button
              onClick={() => setShowPhotoImport(true)}
              style={{
                display: "flex", alignItems: "center", gap: 14,
                width: "100%", padding: "16px 18px",
                background: "linear-gradient(135deg, #eba99a 0%, #e09788 50%, #d48978 100%)",
                border: "none",
                borderRadius: 14, cursor: "pointer",
                marginBottom: 12,
                transition: "transform 0.15s, box-shadow 0.15s",
                boxShadow: "0 4px 16px rgba(196,126,108,0.2)",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 6px 20px rgba(196,126,108,0.3)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 4px 16px rgba(196,126,108,0.2)"; }}
            >
              <div style={{ textAlign: "left", flex: 1 }}>
                <p style={{ margin: 0, fontFamily: FL, fontSize: 15, fontWeight: 700, color: "#fff", textShadow: "0 1px 2px rgba(0,0,0,0.15)" }}>
                  {t("myplace.create_trip")}
                </p>
                <p style={{ margin: 0, fontFamily: FL, fontSize: 12, color: "#fff" }}>
                  {t("myplace.create_trip_desc")}
                </p>
              </div>
              {/* Map + photos illustration */}
              <div style={{ position: "relative", width: 64, height: 52, flexShrink: 0, borderRadius: 12, overflow: "hidden" }}>
                {/* Map base */}
                <svg style={{ position: "absolute", inset: 0 }} viewBox="0 0 64 52">
                  {/* City background */}
                  <rect x="0" y="0" width="64" height="52" rx="8" fill="rgba(220,215,210,0.45)" />
                  {/* Buildings + Roads - rotated together */}
                  <g transform="rotate(-12, 32, 26)">
                    {/* Building blocks */}
                    <rect x="2" y="2" width="13" height="10" rx="1.5" fill="rgba(200,195,188,0.5)" />
                    <rect x="2" y="17" width="13" height="12" rx="1.5" fill="rgba(190,185,178,0.4)" />
                    <rect x="2" y="34" width="8" height="10" rx="1.5" fill="rgba(195,190,183,0.45)" />
                    <rect x="12" y="34" width="5" height="10" rx="1.5" fill="rgba(185,180,173,0.4)" />
                    <rect x="21" y="2" width="18" height="10" rx="1.5" fill="rgba(195,190,183,0.45)" />
                    <rect x="21" y="17" width="8" height="12" rx="1.5" fill="rgba(185,180,173,0.4)" />
                    <rect x="32" y="17" width="7" height="12" rx="1.5" fill="rgba(200,195,188,0.5)" />
                    <rect x="21" y="34" width="18" height="10" rx="1.5" fill="rgba(190,185,178,0.4)" />
                    <rect x="45" y="2" width="16" height="10" rx="1.5" fill="rgba(190,185,178,0.45)" />
                    <rect x="45" y="17" width="16" height="12" rx="1.5" fill="rgba(200,195,188,0.5)" />
                    <rect x="45" y="34" width="16" height="10" rx="1.5" fill="rgba(195,190,183,0.4)" />
                    <line x1="-10" y1="14" x2="74" y2="14" stroke="rgba(255,255,255,0.7)" strokeWidth="2" strokeLinecap="round" />
                    <line x1="-10" y1="32" x2="74" y2="32" stroke="rgba(255,255,255,0.6)" strokeWidth="1.6" strokeLinecap="round" />
                    <line x1="-10" y1="46" x2="74" y2="46" stroke="rgba(255,255,255,0.4)" strokeWidth="0.8" strokeLinecap="round" />
                    <line x1="18" y1="-6" x2="18" y2="58" stroke="rgba(255,255,255,0.65)" strokeWidth="1.8" strokeLinecap="round" />
                    <line x1="42" y1="-6" x2="42" y2="58" stroke="rgba(255,255,255,0.6)" strokeWidth="1.5" strokeLinecap="round" />
                  </g>
                  {/* Route */}
                  <path d="M12 38 Q22 12 36 20 Q50 28 54 10" fill="none" stroke="rgba(255,255,255,0.7)" strokeWidth="1.5" strokeDasharray="3 2" />
                  {/* Pin dots */}
                  <circle cx="12" cy="38" r="2.5" fill="#fff" opacity="0.9" />
                  <circle cx="36" cy="20" r="2.5" fill="#fff" opacity="0.9" />
                  <circle cx="54" cy="10" r="2.5" fill="#fff" opacity="0.9" />
                </svg>
                {/* Photo pins */}
                <div style={{
                  position: "absolute", left: 3, top: 28,
                  width: 18, height: 18, borderRadius: 4,
                  background: "rgba(255,255,255,0.6)",
                  border: "1.5px solid #fff",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 10, boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
                }}>🍜</div>
                <div style={{
                  position: "absolute", left: 27, top: 10,
                  width: 18, height: 18, borderRadius: 4,
                  background: "rgba(255,255,255,0.6)",
                  border: "1.5px solid #fff",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 10, boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
                }}>☕</div>
                <div style={{
                  position: "absolute", left: 45, top: 0,
                  width: 18, height: 18, borderRadius: 4,
                  background: "rgba(255,255,255,0.6)",
                  border: "1.5px solid #fff",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 10, boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
                }}>📸</div>
              </div>
            </button>
            <TripsSection
              savedTrips={savedTrips}
              tripsLoading={tripsLoading}
              deletingTripId={deletingTripId}
              onViewTrip={handleViewSavedTrip}
              onEditTrip={handleEditTrip}
              onDeleteTrip={handleDeleteTrip}
              onShareTrip={handleShareTrip}
            />
            {shareToast && (
              <div style={{
                position: "fixed", bottom: 100, left: "50%", transform: "translateX(-50%)",
                background: "#2f3430", color: "#fff", padding: "10px 20px",
                borderRadius: 10, fontFamily: FL, fontSize: 13, fontWeight: 600,
                zIndex: 200, boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
              }}>
                {shareToast}
              </div>
            )}
            {editingTrip && (
              <PhotoPlaceImport
                editTrip={editingTrip}
                onClose={() => setEditingTrip(null)}
                extraSection={
                  <CollaboratorsSection
                    tripId={editingTrip.id}
                    tripOwnerId={editingTrip.user_id}
                    collaborators={editingTrip.collaborators || []}
                  />
                }
                onViewTrip={(tripData) => {
                  // tripData has id from editTrip — update existing trip
                  handleSaveEdit({
                    id: tripData.id,
                    name: tripData.name,
                    date: tripData.date,
                    stops: tripData.stops.map((s, i) => ({
                      place_id: s.place_id || null,
                      name: s.name,
                      lat: s.lat,
                      lng: s.lng,
                      visited_at: s.visited_at || null,
                      memo: s.memo || null,
                      photos: (s.photos || []).filter((p) => p.isExisting).map((p) => ({ url: p.blobUrl, type: "existing" })),
                      newPhotos: (s.photos || []).filter((p) => !p.isExisting && p.blobUrl).map((p) => ({ url: p.blobUrl, file: p.file, type: "new" })),
                    })),
                  });
                  setEditingTrip(null);
                  if (tripData.stops?.length > 0) {
                    onViewTrip?.(tripData);
                  }
                }}
              />
            )}
          </>
        )}

        {subTab === "lists" && (
          <CuratedLists personalPlaces={personalPlaces} />
        )}

        {subTab === "best" && (
          <BestSection
            myBestPicks={myBestPicks}
            personalPlaces={personalPlaces}
            onBestPickAdd={onBestPickAdd}
            onBestPickRemove={onBestPickRemove}
          />
        )}
      </div>

      {/* Modals */}
      {pendingPlace && (
        <SavePlaceModal place={pendingPlace} onSave={handleSavePlace} onClose={() => setPendingPlace(null)} isGuest={!user} />
      )}

      {showImport && (
        <GoogleMapsImportModal
          initialUrl={importUrl}
          onClose={() => { setShowImport(false); setImportUrl(""); setQuery(""); }}
          onDone={() => onDataChange?.()}
          personalPlaces={personalPlaces}
        />
      )}

      {showPhotoImport && (
        <PhotoPlaceImport
          onPlaceAdded={(place) => { onPlaceAdded?.(place); onDataChange?.(); }}
          onClose={() => setShowPhotoImport(false)}
          onViewTrip={onViewTrip}
          savedPlaces={personalPlaces}
        />
      )}
    </div>
  );
}


/* ── Places Section ── */
function PlacesSection({ personalPlaces, myFolders, onPlaceClick, onViewMap, mobile }) {
  const { t } = useTranslation();
  const STATUS_EMOJI = { want_to_go: "\u{1F516}", visited: "\u2705", want_revisit: "\u2764\uFE0F" };
  const counts = {};
  personalPlaces.forEach((p) => { counts[p.status] = (counts[p.status] || 0) + 1; });

  // Group by folder
  const grouped = {};
  personalPlaces.forEach((p) => {
    const fid = p.folder_id || 0;
    (grouped[fid] = grouped[fid] || []).push(p);
  });
  const orderedFids = myFolders.map((f) => f.id).filter((id) => grouped[id]);
  if (grouped[0]) orderedFids.push(0);

  if (personalPlaces.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: "48px 20px" }}>
        <span className="material-symbols-outlined" style={{ fontSize: 48, color: C.outlineVariant, marginBottom: 12, display: "block" }}>
          add_location
        </span>
        <p style={{ fontFamily: FH, fontStyle: "italic", fontSize: 16, color: C.onSurfaceVariant }}>
          {t("profile.no_places")}
        </p>
        <p style={{ fontFamily: FL, fontSize: 13, color: C.outlineVariant }}>
          Search for a place and save it to your map
        </p>
      </div>
    );
  }

  return (
    <>
      {/* Stats bar */}
      <div style={{
        display: "flex", alignItems: "center", gap: 12,
        padding: "8px 12px", background: C.surfaceLowest, borderRadius: 10,
        marginBottom: 14, boxShadow: "0 1px 6px rgba(47,52,48,0.04)",
      }}>
        <span style={{ fontFamily: FL, fontSize: 18, fontWeight: 700, color: C.onSurface }}>
          {personalPlaces.length}
        </span>
        <span style={{ fontFamily: FL, fontSize: 12, color: C.outlineVariant }}>{t("myplace.places_count")}</span>
        <div style={{ flex: 1 }} />
        {Object.entries(counts).map(([status, count]) => (
          <span key={status} style={{ fontFamily: FL, fontSize: 12, color: C.onSurfaceVariant }}>
            {STATUS_EMOJI[status] || "\u{1F4CD}"} {count}
          </span>
        ))}
        <button onClick={onViewMap} style={{
          padding: "4px 10px", border: "1px solid rgba(101,93,84,0.15)", borderRadius: 6,
          background: "none", fontFamily: FL, fontSize: 11, fontWeight: 600,
          color: C.primary, cursor: "pointer",
        }}>
          {t("myplace.map")}
        </button>
      </div>

      {/* Grid by collection */}
      {orderedFids.map((fid) => {
        const folder = myFolders.find((f) => f.id === fid);
        const color = folder?.color || C.outlineVariant;
        const name = folder?.name || t("myplace.uncategorized");
        const items = grouped[fid];
        return (
          <div key={fid} style={{ marginBottom: 20 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
              <div style={{ width: 3, height: 14, borderRadius: 2, background: color, flexShrink: 0 }} />
              <p style={{ margin: 0, fontFamily: FL, fontSize: 13, fontWeight: 700, color: C.onSurfaceVariant }}>
                {name} <span style={{ fontWeight: 500, color: C.outlineVariant }}>({items.length})</span>
              </p>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 2 }}>
              {items.map((p) => {
                const photo = p.photo_urls?.[0] || p.photo_url;
                const sc = STATUS_COLOR[p.status];
                return (
                  <div
                    key={p.id}
                    onClick={() => onPlaceClick?.(p)}
                    style={{
                      position: "relative", aspectRatio: "1", overflow: "hidden",
                      background: photo ? "#f0efec" : `linear-gradient(135deg, ${C.primaryDim}18, ${C.primary}0a)`,
                      cursor: onPlaceClick ? "pointer" : "default",
                    }}
                  >
                    {photo ? (
                      <img src={photo} alt={p.name}
                        style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                    ) : (
                      <div style={{
                        width: "100%", height: "100%",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        flexDirection: "column", gap: 4,
                      }}>
                        <span className="material-symbols-outlined" style={{ fontSize: 20, color: C.outlineVariant }}>add_a_photo</span>
                      </div>
                    )}
                    <div style={{
                      position: "absolute", bottom: 0, left: 0, right: 0,
                      background: photo ? "linear-gradient(transparent, rgba(0,0,0,0.55))" : "none",
                      padding: "16px 6px 5px",
                    }}>
                      <p style={{
                        margin: 0, fontFamily: FL, fontSize: 12, fontWeight: 700,
                        color: photo ? "#fff" : C.onSurface, lineHeight: 1.3, textAlign: "center",
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                      }}>{p.name}</p>
                    </div>
                    <div style={{
                      position: "absolute", top: 4, left: 4,
                      width: 8, height: 8, borderRadius: "50%",
                      background: sc?.color || C.outlineVariant,
                      border: "1.5px solid rgba(255,255,255,0.8)",
                    }} />
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </>
  );
}


/* ── Trips Section ── */
function TripsSection({ savedTrips, tripsLoading, deletingTripId, onViewTrip, onEditTrip, onDeleteTrip, onShareTrip }) {
  const { t } = useTranslation();
  if (tripsLoading) {
    return (
      <p style={{ fontFamily: FL, fontSize: 13, color: C.outlineVariant, textAlign: "center", padding: 40 }}>
        {t("myplace.loading")}
      </p>
    );
  }

  if (savedTrips.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: "48px 20px" }}>
        <span className="material-symbols-outlined" style={{ fontSize: 48, color: C.outlineVariant, marginBottom: 12, display: "block" }}>
          trip_origin
        </span>
        <p style={{ fontFamily: FH, fontStyle: "italic", fontSize: 16, color: C.onSurfaceVariant }}>
          {t("myplace.no_trips")}
        </p>
        <p style={{ fontFamily: FL, fontSize: 13, color: C.outlineVariant, lineHeight: 1.6 }}>
          {t("myplace.no_trips_desc")}
        </p>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {savedTrips.map((trip) => (
        <div
          key={trip.id}
          onClick={() => onViewTrip(trip.id)}
          style={{
            display: "flex", alignItems: "center", gap: 12,
            padding: "12px 14px", borderRadius: 12,
            background: C.surfaceLowest,
            boxShadow: "0 1px 6px rgba(47,52,48,0.04)",
            cursor: "pointer", transition: "background 0.15s",
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = C.surfaceLow}
          onMouseLeave={(e) => e.currentTarget.style.background = C.surfaceLowest}
        >
          {trip.thumbnail_url ? (
            <img src={trip.thumbnail_url} alt="" style={{
              width: 48, height: 48, borderRadius: 10, objectFit: "cover", flexShrink: 0,
            }} />
          ) : (
            <div style={{
              width: 48, height: 48, borderRadius: 10, flexShrink: 0,
              background: "#FFF8F0", border: "1px solid #F0DCC8",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <span className="material-symbols-outlined" style={{ fontSize: 22, color: "#E8943A" }}>
                trip_origin
              </span>
            </div>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{
              margin: 0, fontFamily: FL, fontSize: 14, fontWeight: 700, color: C.onSurface,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>
              {trip.name}
            </p>
            <p style={{ margin: "3px 0 0", fontFamily: FL, fontSize: 12, color: C.outlineVariant }}>
              {trip.stop_count} {t("myplace.stops")}{trip.date ? ` \u00B7 ${trip.date}` : ""}
            </p>
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); onShareTrip(trip.id); }}
            style={{
              background: "none", border: "none", padding: 6,
              cursor: "pointer", color: C.outlineVariant, flexShrink: 0,
              opacity: 0.5, transition: "opacity 0.15s",
            }}
            onMouseEnter={(e) => e.currentTarget.style.opacity = 1}
            onMouseLeave={(e) => e.currentTarget.style.opacity = 0.5}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>share</span>
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onEditTrip(trip.id); }}
            style={{
              background: "none", border: "none", padding: 6,
              cursor: "pointer", color: C.outlineVariant, flexShrink: 0,
              opacity: 0.5, transition: "opacity 0.15s",
            }}
            onMouseEnter={(e) => e.currentTarget.style.opacity = 1}
            onMouseLeave={(e) => e.currentTarget.style.opacity = 0.5}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>edit</span>
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onDeleteTrip(trip.id); }}
            disabled={deletingTripId === trip.id}
            style={{
              background: "none", border: "none", padding: 6,
              cursor: "pointer", color: C.outlineVariant, flexShrink: 0,
              opacity: deletingTripId === trip.id ? 0.4 : 0.5,
              transition: "opacity 0.15s",
            }}
            onMouseEnter={(e) => e.currentTarget.style.opacity = 1}
            onMouseLeave={(e) => e.currentTarget.style.opacity = 0.5}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>delete</span>
          </button>
        </div>
      ))}
    </div>
  );
}


/* ── Best Picks Section ── */
function BestSection({ myBestPicks, personalPlaces, onBestPickAdd, onBestPickRemove }) {
  const [pickerCat, setPickerCat] = useState(null);
  const { user } = useUser();

  return (
    <>
      {/* Share links */}
      {user && (
        <div style={{
          display: "flex", gap: 8, marginBottom: 14,
          padding: "8px 12px", background: C.surfaceLowest, borderRadius: 10,
          boxShadow: "0 1px 6px rgba(47,52,48,0.04)",
        }}>
          <span style={{ fontFamily: FL, fontSize: 12, fontWeight: 600, color: C.outlineVariant, flex: 1, display: "flex", alignItems: "center" }}>
            Share your Best Picks
          </span>
          <button onClick={() => {
            const url = `${FRONTEND_URL}/best/${encodeURIComponent(user.nickname)}`;
            navigator.clipboard?.writeText(url);
          }} style={{
            background: "none", border: "none", cursor: "pointer", padding: "2px 6px",
            color: C.outlineVariant, display: "flex", alignItems: "center",
          }}>
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>link</span>
          </button>
          {navigator.share && (
            <button onClick={() => {
              const url = `${FRONTEND_URL}/best/${encodeURIComponent(user.nickname)}`;
              navigator.share({ title: `${user.nickname}'s Best Picks`, url }).catch(() => {});
            }} style={{
              background: "none", border: "none", cursor: "pointer", padding: "2px 6px",
              color: C.outlineVariant, display: "flex", alignItems: "center",
            }}>
              <span className="material-symbols-outlined" style={{ fontSize: 16 }}>ios_share</span>
            </button>
          )}
        </div>
      )}

      {BEST_CATEGORIES.map(({ key, emoji, label }) => {
        const picks = myBestPicks[key] || [];
        return (
          <div key={key} style={{ marginBottom: 16 }}>
            <p style={{
              margin: "0 0 8px", fontFamily: FL, fontSize: 13, fontWeight: 600,
              color: C.onSurfaceVariant,
            }}>
              {emoji} {label}
              <span style={{ fontWeight: 400, color: C.outlineVariant, marginLeft: 6, fontSize: 11 }}>
                {picks.length}/5
              </span>
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {picks.map((p) => (
                <div key={p.id} style={{
                  display: "flex", alignItems: "center", gap: 8,
                  padding: "8px 10px", borderRadius: 8, background: C.surfaceLow,
                }}>
                  <span style={{
                    flex: 1, fontFamily: FL, fontSize: 14, fontWeight: 600, color: C.onSurface,
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  }}>
                    {p.name}
                  </span>
                  {p.address && (
                    <span style={{
                      fontFamily: FL, fontSize: 12, color: C.outlineVariant,
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                      maxWidth: 100, flexShrink: 1,
                    }}>
                      {p.address}
                    </span>
                  )}
                  <button
                    onClick={() => onBestPickRemove(p.id)}
                    style={{
                      width: 22, height: 22, padding: 0, border: "none",
                      background: "none", cursor: "pointer", flexShrink: 0,
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}
                  >
                    <span className="material-symbols-outlined" style={{ fontSize: 14, color: C.outlineVariant }}>close</span>
                  </button>
                </div>
              ))}
              {picks.length < 5 && (
                <button
                  onClick={() => setPickerCat({ key, label: `${emoji} ${label}` })}
                  style={{
                    display: "flex", alignItems: "center", justifyContent: "center",
                    padding: "9px 10px", borderRadius: 8,
                    border: `1.5px dashed ${C.container}`, background: "none",
                    cursor: "pointer", fontFamily: FL, fontSize: 13, fontStyle: "italic",
                    color: C.outlineVariant, transition: "border-color 0.15s, background 0.15s",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = C.primary; e.currentTarget.style.background = C.surfaceLow; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = C.container; e.currentTarget.style.background = "none"; }}
                >
                  + Add a pick
                </button>
              )}
            </div>
          </div>
        );
      })}

      {pickerCat && (
        <BestPickerModal
          category={pickerCat.key}
          categoryLabel={pickerCat.label}
          personalPlaces={personalPlaces}
          myBestPicks={myBestPicks}
          onSelect={(data) => { onBestPickAdd(data); setPickerCat(null); }}
          onClose={() => setPickerCat(null)}
        />
      )}
    </>
  );
}


/* ── Trip Edit Modal ── */

// Haversine distance in meters
function haversineM(lat1, lng1, lat2, lng2) {
  const toRad = (d) => (d * Math.PI) / 180;
  const R = 6371000;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// Merge consecutive coordinate-only stops within 500m
function mergeCoordStops(rawStops) {
  const merged = [];
  for (const stop of rawStops) {
    const isCoordOnly = !stop.place_id && !stop.name;
    const prev = merged[merged.length - 1];
    const prevIsCoordOnly = prev && !prev.place_id && !prev.name;

    if (isCoordOnly && prevIsCoordOnly && prev.lat != null && stop.lat != null) {
      const dist = haversineM(prev.lat, prev.lng, stop.lat, stop.lng);
      if (dist < 500) {
        // Merge: combine photos, keep earlier visited_at
        prev.photos = [...(prev.photos || []), ...(stop.photos || [])];
        prev.newPhotos = [...(prev.newPhotos || []), ...(stop.newPhotos || [])];
        if (!prev.visited_at && stop.visited_at) prev.visited_at = stop.visited_at;
        continue;
      }
    }
    merged.push({ ...stop });
  }
  return merged;
}

function TripEditModal({ trip, onSave, onClose }) {
  const { t } = useTranslation();
  const [name, setName] = useState(trip.name);
  const [editingName, setEditingName] = useState(false);
  const [startDate, setStartDate] = useState(() => {
    const d = trip.date || "";
    return d.includes(" ~ ") ? d.split(" ~ ")[0] : d;
  });
  const [endDate, setEndDate] = useState(() => {
    const d = trip.date || "";
    return d.includes(" ~ ") ? d.split(" ~ ")[1] : "";
  });
  const [stops, setStops] = useState(() => {
    const withPhotos = (trip.stops || []).map((s) => {
      // Collect all photo URLs (single + array)
      const urls = [];
      if (s.photo_url) urls.push(s.photo_url);
      if (s.photo_urls) {
        for (const u of s.photo_urls) {
          if (!urls.includes(u)) urls.push(u);
        }
      }
      return {
        ...s,
        photos: urls.map((url) => ({ url, type: "existing" })),
        newPhotos: [],
      };
    });
    return mergeCoordStops(withPhotos);
  });
  const [isPublic, setIsPublic] = useState(trip.is_public !== false);
  const [saving, setSaving] = useState(false);
  const [addingStop, setAddingStop] = useState(false);
  const [newStopQuery, setNewStopQuery] = useState("");
  const [newStopResults, setNewStopResults] = useState([]);
  const [newStopSearching, setNewStopSearching] = useState(false);

  const moveStop = (from, to) => {
    if (to < 0 || to >= stops.length) return;
    const arr = [...stops];
    const [item] = arr.splice(from, 1);
    arr.splice(to, 0, item);
    setStops(arr);
  };

  const removeStop = (idx) => {
    setStops((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleAddNewStop = async () => {
    if (!newStopQuery.trim()) return;
    setNewStopSearching(true);
    try {
      const { Place } = await window.google.maps.importLibrary("places");
      const { places } = await Place.searchByText({
        textQuery: newStopQuery.trim(),
        fields: ["displayName", "formattedAddress", "location", "id", "types"],
        maxResultCount: 5,
      });
      setNewStopResults((places || []).map((p) => ({
        name: p.displayName,
        address: p.formattedAddress,
        lat: p.location.lat(),
        lng: p.location.lng(),
        google_place_id: p.id,
      })));
    } catch {
      setNewStopResults([]);
    }
    setNewStopSearching(false);
  };

  const selectNewStop = (place) => {
    setStops((prev) => [...prev, {
      place_id: null,
      name: place.name,
      lat: place.lat,
      lng: place.lng,
      visited_at: null,
      photo_url: null,
      photo_urls: [],
      photos: [],
      newPhotos: [],
    }]);
    setAddingStop(false);
    setNewStopQuery("");
    setNewStopResults([]);
  };

  const handleSave = async () => {
    setSaving(true);
    const date = startDate && endDate && endDate !== startDate
      ? `${startDate} ~ ${endDate}`
      : startDate || null;
    await onSave({ id: trip.id, name, date, is_public: isPublic, stops });
    setSaving(false);
  };

  return createPortal(
    <div
      onClick={(e) => e.target === e.currentTarget && onClose()}
      style={{
        position: "fixed", inset: 0,
        background: "rgba(47,52,48,0.4)",
        backdropFilter: "blur(4px)",
        display: "flex", alignItems: "flex-end", justifyContent: "center",
        zIndex: 100,
      }}
    >
      <div style={{
        background: C.bg, borderRadius: "20px 20px 0 0",
        width: "100%", maxWidth: 520, maxHeight: "85vh",
        overflowY: "auto", overflowX: "hidden",
        boxShadow: "0 -8px 40px rgba(47,52,48,0.12)",
      }}>
        {/* Handle */}
        <div style={{ display: "flex", justifyContent: "center", padding: "14px 0 6px" }}>
          <div style={{ width: 32, height: 3, background: C.outlineVariant, borderRadius: 2, opacity: 0.4 }} />
        </div>

        {/* Header */}
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          padding: "8px 24px 16px",
        }}>
          <div>
            <p style={{ margin: 0, fontFamily: FL, fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.15em", color: C.outlineVariant }}>
              Edit trip
            </p>
            <h3 style={{ margin: "4px 0 0", fontFamily: FH, fontStyle: "italic", fontSize: 20, fontWeight: 700, color: C.onSurface }}>
              Trip Editor
            </h3>
          </div>
          <button onClick={onClose} style={{
            background: "none", border: "none", fontSize: 20, color: C.outlineVariant, cursor: "pointer", padding: 4,
          }}>&#10005;</button>
        </div>

        <div style={{ padding: "0 24px 24px" }}>
          {/* Trip header — like PhotoPlaceImport TripSection */}
          <div style={{
            display: "flex", alignItems: "center", gap: 8, marginBottom: 14,
            padding: "8px 12px",
            background: "#FFF8F0",
            borderRadius: 10,
            border: "1px solid #F0DCC8",
          }}>
            <span className="material-symbols-outlined" style={{ fontSize: 18, color: "#E8943A" }}>
              trip_origin
            </span>
            {editingName ? (
              <input
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                onBlur={() => setEditingName(false)}
                onKeyDown={(e) => e.key === "Enter" && setEditingName(false)}
                placeholder={t("myplace.trip_name")}
                style={{
                  flex: 1, padding: "4px 8px",
                  background: "#fff", border: `1px solid ${C.outlineVariant}44`,
                  borderRadius: 6, fontFamily: FL, fontSize: 13,
                  fontWeight: 600, color: C.onSurface, outline: "none",
                }}
              />
            ) : (
              <p
                onClick={() => setEditingName(true)}
                style={{
                  margin: 0, flex: 1,
                  fontFamily: FL, fontSize: 13, fontWeight: 700,
                  color: C.onSurface, cursor: "pointer",
                }}
              >
                {name || t("myplace.untitled_trip")}
                <span style={{ fontWeight: 400, fontSize: 11, color: C.outlineVariant, marginLeft: 6 }}>
                  tap to edit
                </span>
              </p>
            )}
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                style={{
                  padding: "2px 4px", background: "#fff", border: `1px solid #F0DCC8`,
                  borderRadius: 4, fontFamily: FL, fontSize: 10, color: C.onSurface,
                  outline: "none", width: 105,
                }}
              />
              <span style={{ fontFamily: FL, fontSize: 10, color: C.outlineVariant }}>~</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                style={{
                  padding: "2px 4px", background: "#fff", border: `1px solid #F0DCC8`,
                  borderRadius: 4, fontFamily: FL, fontSize: 10, color: C.onSurface,
                  outline: "none", width: 105,
                }}
              />
            </div>
          </div>

          {/* Summary */}
          <div style={{
            display: "flex", gap: 12, marginBottom: 14,
            padding: "10px 16px", background: C.surfaceLow, borderRadius: 10,
          }}>
            <div style={{ flex: 1, textAlign: "center" }}>
              <p style={{ margin: 0, fontFamily: FL, fontSize: 18, fontWeight: 700, color: "#E8943A" }}>{stops.length}</p>
              <p style={{ margin: "2px 0 0", fontFamily: FL, fontSize: 10, color: C.outlineVariant }}>{t("myplace.stops_label")}</p>
            </div>
            <div style={{ flex: 1, textAlign: "center" }}>
              <p style={{ margin: 0, fontFamily: FL, fontSize: 18, fontWeight: 700, color: C.primary }}>
                {stops.reduce((n, s) => n + (s.photos?.length || 0) + (s.newPhotos?.length || 0), 0)}
              </p>
              <p style={{ margin: "2px 0 0", fontFamily: FL, fontSize: 10, color: C.outlineVariant }}>{t("myplace.photos_label")}</p>
            </div>
          </div>

          {/* Public/Private toggle */}
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "10px 14px", marginBottom: 14,
            background: C.surfaceLow, borderRadius: 10,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span className="material-symbols-outlined" style={{ fontSize: 16, color: isPublic ? "#1D9E75" : "#8a8e8a" }}>
                {isPublic ? "public" : "lock"}
              </span>
              <span style={{ fontFamily: FL, fontSize: 13, fontWeight: 600, color: C.onSurface }}>
                {isPublic ? t("profile.public_account") : t("profile.private_account")}
              </span>
            </div>
            <button
              onClick={() => setIsPublic(!isPublic)}
              style={{
                position: "relative", width: 40, height: 22,
                background: isPublic ? "#1D9E75" : "#c7c4bf",
                borderRadius: 11, border: "none", cursor: "pointer",
                transition: "background 0.2s",
              }}
            >
              <div style={{
                position: "absolute", top: 2,
                left: isPublic ? 20 : 2,
                width: 18, height: 18, borderRadius: "50%",
                background: "#fff",
                transition: "left 0.2s",
                boxShadow: "0 1px 3px rgba(0,0,0,0.15)",
              }} />
            </button>
          </div>

          {/* Collaborators */}
          <CollaboratorsSection tripId={trip.id} tripOwnerId={trip.user_id} collaborators={trip.collaborators || []} />

          {/* Timeline stops */}
          <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
            {stops.map((stop, i) => {
              const isLast = i === stops.length - 1;
              return (
                <div key={i} style={{ display: "flex", gap: 0 }}>
                  {/* Timeline rail */}
                  <div style={{
                    display: "flex", flexDirection: "column", alignItems: "center",
                    width: 48, flexShrink: 0,
                  }}>
                    {/* Time */}
                    <p style={{
                      margin: 0, fontFamily: FL, fontSize: 10, fontWeight: 600,
                      color: "#E8943A", whiteSpace: "nowrap",
                    }}>
                      {stop.visited_at || ""}
                    </p>
                    {/* Dot */}
                    <div style={{
                      width: 8, height: 8, borderRadius: "50%",
                      background: "#E8943A", margin: "4px 0 2px", flexShrink: 0,
                    }} />
                    {/* Line */}
                    {!isLast && (
                      <div style={{
                        width: 2, flex: 1, minHeight: 12,
                        background: "linear-gradient(#E8943A, #E8943A44)",
                      }} />
                    )}
                  </div>

                  {/* Stop card */}
                  <div style={{ flex: 1, paddingBottom: isLast ? 0 : 8 }}>
                    <EditStopCard
                      stop={stop}
                      index={i}
                      total={stops.length}
                      onMove={moveStop}
                      onRemove={() => removeStop(i)}
                      onUpdateStop={(updated) => setStops((prev) => prev.map((s, j) => j === i ? updated : s))}
                      onAutoRemove={() => removeStop(i)}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Add stop */}
          {addingStop ? (
            <div style={{
              marginTop: 12, padding: 12,
              background: C.surfaceLowest, borderRadius: 10,
              border: `1px solid ${C.container}`,
            }}>
              <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
                <input
                  autoFocus
                  value={newStopQuery}
                  onChange={(e) => setNewStopQuery(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleAddNewStop()}
                  placeholder={t("myplace.search_place")}
                  style={{
                    flex: 1, padding: "8px 10px",
                    background: C.surfaceLow, border: "none", borderRadius: 6,
                    fontFamily: FL, fontSize: 12, color: C.onSurface,
                    outline: "none", boxSizing: "border-box",
                  }}
                />
                <button
                  onClick={handleAddNewStop}
                  disabled={newStopSearching}
                  style={{
                    padding: "8px 12px",
                    background: C.primary, color: "#fff6ef",
                    border: "none", borderRadius: 6,
                    fontFamily: FL, fontSize: 11, fontWeight: 700,
                    cursor: "pointer", flexShrink: 0,
                  }}
                >
                  {newStopSearching ? "..." : t("myplace.search")}
                </button>
              </div>
              {newStopResults.map((r) => (
                <button
                  key={r.google_place_id}
                  onClick={() => selectNewStop(r)}
                  style={{
                    display: "flex", alignItems: "center", gap: 6,
                    padding: "6px 10px", borderRadius: 8, marginBottom: 4,
                    border: "1.5px solid transparent",
                    background: C.surfaceLow,
                    cursor: "pointer", textAlign: "left", width: "100%",
                  }}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: 14, color: C.outlineVariant }}>place</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ margin: 0, fontFamily: FL, fontSize: 12, fontWeight: 600, color: C.onSurface, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.name}</p>
                    <p style={{ margin: 0, fontFamily: FL, fontSize: 10, color: C.outlineVariant, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.address}</p>
                  </div>
                </button>
              ))}
              <button
                onClick={() => { setAddingStop(false); setNewStopQuery(""); setNewStopResults([]); }}
                style={{
                  background: "none", border: "none", padding: "6px 0",
                  fontFamily: FL, fontSize: 12, color: C.outlineVariant, cursor: "pointer",
                }}
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              onClick={() => setAddingStop(true)}
              style={{
                width: "100%", marginTop: 12, padding: "12px",
                border: `1.5px dashed ${C.outlineVariant}66`,
                borderRadius: 10, background: "none",
                fontFamily: FL, fontSize: 13, color: C.outlineVariant,
                cursor: "pointer",
              }}
            >
              + Add a stop
            </button>
          )}

          {/* Save */}
          <button
            onClick={handleSave}
            disabled={saving || !name.trim()}
            style={{
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
              width: "100%", marginTop: 16, padding: "12px",
              background: saving ? C.container : "#E8943A",
              color: saving ? C.outlineVariant : "#fff",
              border: "none", borderRadius: 10,
              fontFamily: FL, fontSize: 14, fontWeight: 700,
              cursor: saving ? "default" : "pointer",
              boxShadow: saving ? "none" : "0 4px 16px rgba(232,148,58,0.25)",
            }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>save</span>
            {saving ? t("myplace.saving") : t("myplace.save_changes")}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}


/* ── Edit Stop Card (inside TripEditModal) ── */
function EditStopCard({ stop, index, total, onMove, onRemove, onUpdateStop, onAutoRemove }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const addPhotoRef = useRef(null);

  const allPhotos = [
    ...(stop.photos || []),
    ...(stop.newPhotos || []),
  ];

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    try {
      const { Place } = await window.google.maps.importLibrary("places");
      const { places } = await Place.searchByText({
        textQuery: searchQuery.trim(),
        fields: ["displayName", "formattedAddress", "location", "id", "types"],
        maxResultCount: 5,
      });
      setSearchResults((places || []).map((p) => ({
        name: p.displayName,
        address: p.formattedAddress,
        lat: p.location.lat(),
        lng: p.location.lng(),
        google_place_id: p.id,
      })));
    } catch {
      setSearchResults([]);
    }
    setSearching(false);
  };

  const selectPlace = (place) => {
    onUpdateStop({
      ...stop,
      name: place.name,
      lat: place.lat,
      lng: place.lng,
      place_id: null, // new place, not from personal places
    });
    setExpanded(false);
    setSearchQuery("");
    setSearchResults([]);
  };

  const handleAddPhotos = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const newPhotos = files.map((f) => ({
      url: URL.createObjectURL(f),
      file: f,
      type: "new",
    }));
    onUpdateStop({
      ...stop,
      newPhotos: [...(stop.newPhotos || []), ...newPhotos],
    });
    e.target.value = "";
  };

  const removePhoto = (photoIdx) => {
    const existingCount = (stop.photos || []).length;
    let updatedPhotos, updatedNewPhotos;
    if (photoIdx < existingCount) {
      updatedPhotos = stop.photos.filter((_, i) => i !== photoIdx);
      updatedNewPhotos = stop.newPhotos || [];
    } else {
      const newIdx = photoIdx - existingCount;
      updatedPhotos = stop.photos || [];
      updatedNewPhotos = (stop.newPhotos || []).filter((_, i) => i !== newIdx);
    }
    // If no photos remain, auto-remove the stop
    if (updatedPhotos.length === 0 && updatedNewPhotos.length === 0) {
      onAutoRemove?.();
      return;
    }
    onUpdateStop({
      ...stop,
      photos: updatedPhotos,
      newPhotos: updatedNewPhotos,
    });
  };

  return (
    <div style={{
      borderRadius: 12,
      background: "#fff",
      border: `1px solid #e8e8e4`,
    }}>
      {/* Photo strip — horizontally scrollable */}
      <div style={{
        display: "flex", gap: 3, padding: 3,
        overflowX: "auto", scrollbarWidth: "none",
        WebkitOverflowScrolling: "touch",
        touchAction: "pan-x",
        borderRadius: "12px 12px 0 0",
      }}>
        {allPhotos.map((p, i) => {
          const thumbSize = window.innerWidth <= 768 ? 100 : 80;
          return (
            <div key={i} style={{ position: "relative", flexShrink: 0 }}>
              <img
                src={p.url}
                alt=""
                style={{
                  width: thumbSize, height: thumbSize,
                  objectFit: "cover", display: "block",
                  borderRadius: i === 0 ? "10px 0 0 0" : 0,
                }}
              />
              <button
                onClick={(e) => { e.stopPropagation(); removePhoto(i); }}
                style={{
                  position: "absolute", top: 4, right: 4,
                  width: 24, height: 24, borderRadius: "50%",
                  background: "rgba(0,0,0,0.5)", border: "none",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  cursor: "pointer", padding: 0,
                }}
              >
                <span className="material-symbols-outlined" style={{ fontSize: 14, color: "#fff" }}>close</span>
              </button>
            </div>
          );
        })}
        {/* Add photo button */}
        <div
          onClick={() => addPhotoRef.current?.click()}
          style={{
            width: window.innerWidth <= 768 ? 100 : 80,
            height: window.innerWidth <= 768 ? 100 : 80,
            flexShrink: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
            background: C.surfaceLow, cursor: "pointer",
            borderRadius: allPhotos.length === 0 ? "10px 10px 0 0" : "0 10px 0 0",
          }}
        >
          <span className="material-symbols-outlined" style={{ fontSize: 28, color: C.outlineVariant }}>add_photo_alternate</span>
        </div>
        <input
          ref={addPhotoRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleAddPhotos}
          style={{ display: "none" }}
        />
      </div>

      {/* Place info — tap to expand */}
      <div
        onClick={() => setExpanded(!expanded)}
        style={{ padding: "10px 14px", cursor: "pointer" }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span className="material-symbols-outlined" style={{ fontSize: 16, color: C.primary }}>place</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{
              margin: 0, fontFamily: FL, fontSize: 13, fontWeight: 700,
              color: C.onSurface,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>
              {stop.name || `${stop.lat?.toFixed(4)}, ${stop.lng?.toFixed(4)}`}
            </p>
            {stop.visited_at && (
              <p style={{ margin: 0, fontFamily: FL, fontSize: 10, color: C.outlineVariant }}>
                {stop.visited_at}
              </p>
            )}
          </div>
          {/* Reorder + remove */}
          <button
            onClick={(e) => { e.stopPropagation(); onMove(index, index - 1); }}
            disabled={index === 0}
            style={{
              background: "none", border: "none", padding: 2,
              cursor: index === 0 ? "default" : "pointer",
              color: index === 0 ? C.container : C.outlineVariant, flexShrink: 0,
            }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>arrow_upward</span>
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onMove(index, index + 1); }}
            disabled={index === total - 1}
            style={{
              background: "none", border: "none", padding: 2,
              cursor: index === total - 1 ? "default" : "pointer",
              color: index === total - 1 ? C.container : C.outlineVariant, flexShrink: 0,
            }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>arrow_downward</span>
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onRemove(); }}
            style={{
              background: "none", border: "none", padding: 2,
              cursor: "pointer", color: C.error, flexShrink: 0, opacity: 0.6,
            }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>close</span>
          </button>
          <span className="material-symbols-outlined" style={{
            fontSize: 16, color: C.outlineVariant,
            transform: expanded ? "rotate(180deg)" : "none",
            transition: "transform 0.2s",
          }}>
            expand_more
          </span>
        </div>
      </div>

      {/* Memo input */}
      <div style={{ padding: "0 14px 8px" }}>
        <textarea
          value={stop.memo || ""}
          onChange={(e) => onUpdateStop({ ...stop, memo: e.target.value })}
          placeholder={t("place.notes_placeholder")}
          rows={2}
          style={{
            width: "100%", padding: "8px 10px",
            background: C.surfaceLow, border: "none", borderRadius: 6,
            fontFamily: FL, fontSize: 12, color: C.onSurface,
            outline: "none", boxSizing: "border-box",
            resize: "vertical", minHeight: 36, lineHeight: 1.5,
          }}
        />
      </div>

      {/* Expanded: change place */}
      {expanded && (
        <div style={{ padding: "0 14px 14px" }}>
          {/* Search bar */}
          <div style={{ display: "flex", gap: 6, marginBottom: searchResults.length > 0 ? 8 : 0 }}>
            <input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              placeholder={t("myplace.search_different")}
              style={{
                flex: 1, padding: "8px 10px",
                background: C.surfaceLow, border: "none", borderRadius: 6,
                fontFamily: FL, fontSize: 12, color: C.onSurface,
                outline: "none", boxSizing: "border-box",
              }}
            />
            <button
              onClick={handleSearch}
              disabled={searching}
              style={{
                padding: "8px 12px",
                background: C.primary, color: "#fff6ef",
                border: "none", borderRadius: 6,
                fontFamily: FL, fontSize: 11, fontWeight: 700,
                cursor: "pointer", flexShrink: 0,
              }}
            >
              {searching ? "..." : t("myplace.search")}
            </button>
          </div>

          {/* Search results */}
          {searchResults.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {searchResults.map((r) => (
                <button
                  key={r.google_place_id}
                  onClick={() => selectPlace(r)}
                  style={{
                    display: "flex", alignItems: "center", gap: 6,
                    padding: "6px 10px", borderRadius: 8,
                    border: "1.5px solid transparent",
                    background: C.surfaceLow,
                    cursor: "pointer", textAlign: "left", width: "100%",
                  }}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: 14, color: C.outlineVariant }}>search</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ margin: 0, fontFamily: FL, fontSize: 12, fontWeight: 600, color: C.onSurface, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.name}</p>
                    <p style={{ margin: 0, fontFamily: FL, fontSize: 10, color: C.outlineVariant, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.address}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


/* ── Collaborators Section (inside TripEditModal) ── */

function CollaboratorsSection({ tripId, tripOwnerId, collaborators: initial }) {
  const { t } = useTranslation();
  const { user } = useUser();
  const [collabs, setCollabs] = useState(initial || []);
  const [showAdd, setShowAdd] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const searchTimer = useRef(null);

  // Owner: either in collaborators list with role "owner", or is the trip creator (for pre-migration trips)
  const isOwner = collabs.some((c) => c.user_id === user?.user_id && c.role === "owner")
    || tripOwnerId === user?.user_id;

  const handleSearch = (q) => {
    setQuery(q);
    clearTimeout(searchTimer.current);
    if (q.trim().length < 2) { setResults([]); return; }
    searchTimer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await axios.get(`${API_BASE}/follows/${user.user_id}/following`);
        const following = res.data || [];
        const filtered = following.filter((u) =>
          u.nickname.toLowerCase().includes(q.toLowerCase()) &&
          !collabs.some((c) => c.user_id === u.id)
        );
        setResults(filtered.slice(0, 5));
      } catch {} finally { setSearching(false); }
    }, 300);
  };

  const handleAdd = async (targetUserId) => {
    try {
      const res = await axios.post(`${API_BASE}/trips/${tripId}/collaborators`, { user_id: targetUserId });
      setCollabs((prev) => [...prev, res.data]);
      setResults((prev) => prev.filter((r) => r.id !== targetUserId));
      setQuery("");
    } catch {}
  };

  const handleRemove = async (targetUserId) => {
    try {
      await axios.delete(`${API_BASE}/trips/${tripId}/collaborators/${targetUserId}`);
      setCollabs((prev) => prev.filter((c) => c.user_id !== targetUserId));
    } catch {}
  };

  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <p style={{ margin: 0, fontFamily: FL, fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.15em", color: C.outlineVariant }}>
          Collaborators ({collabs.length})
        </p>
        {isOwner && (
          <button
            onClick={() => setShowAdd(!showAdd)}
            style={{
              padding: "3px 10px", border: `1px solid ${C.outlineVariant}44`,
              borderRadius: 999, background: "none",
              fontFamily: FL, fontSize: 10, fontWeight: 600, color: C.primary,
              cursor: "pointer",
            }}
          >{showAdd ? t("myplace.done") : t("myplace.add")}</button>
        )}
      </div>

      {/* Collaborator list */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: showAdd ? 10 : 0 }}>
        {collabs.map((c) => (
          <div key={c.user_id} style={{
            display: "flex", alignItems: "center", gap: 6,
            padding: "4px 10px 4px 4px",
            background: C.surfaceLow, borderRadius: 999,
          }}>
            <div style={{
              width: 22, height: 22, borderRadius: "50%", flexShrink: 0,
              background: c.profile_photo_url
                ? `url(${c.profile_photo_url}) center/cover`
                : `linear-gradient(135deg, #595149, #655d54)`,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontFamily: FL, fontSize: 9, color: "#fff6ef", fontWeight: 700,
            }}>
              {!c.profile_photo_url && (c.nickname?.[0] || "?").toUpperCase()}
            </div>
            <span style={{ fontFamily: FL, fontSize: 11, fontWeight: 600, color: C.onSurface }}>
              {c.nickname}
            </span>
            {c.role === "owner" && (
              <span style={{ fontFamily: FL, fontSize: 9, color: "#E8943A", fontWeight: 700 }}>{t("myplace.owner")}</span>
            )}
            {isOwner && c.role !== "owner" && (
              <button
                onClick={() => handleRemove(c.user_id)}
                style={{
                  background: "none", border: "none", padding: 0, cursor: "pointer",
                  fontSize: 12, color: C.outlineVariant, lineHeight: 1,
                }}
              >&times;</button>
            )}
          </div>
        ))}
      </div>

      {/* Add collaborator search */}
      {showAdd && isOwner && (
        <div>
          <input
            value={query}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder={t("myplace.search_following")}
            autoFocus
            style={{
              width: "100%", padding: "8px 12px",
              background: C.surfaceLow, border: `1px solid ${C.outlineVariant}33`,
              borderRadius: 8, fontFamily: FL, fontSize: 12, color: C.onSurface,
              outline: "none", boxSizing: "border-box",
            }}
          />
          {results.length > 0 && (
            <div style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 4 }}>
              {results.map((u) => (
                <button
                  key={u.id}
                  onClick={() => handleAdd(u.id)}
                  style={{
                    display: "flex", alignItems: "center", gap: 8,
                    padding: "6px 10px", background: C.surfaceLow,
                    border: "none", borderRadius: 8, cursor: "pointer",
                    fontFamily: FL, fontSize: 12, color: C.onSurface,
                    textAlign: "left",
                  }}
                >
                  <div style={{
                    width: 24, height: 24, borderRadius: "50%", flexShrink: 0,
                    background: u.profile_photo_url
                      ? `url(${u.profile_photo_url}) center/cover`
                      : `linear-gradient(135deg, #595149, #655d54)`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontFamily: FL, fontSize: 10, color: "#fff6ef", fontWeight: 700,
                  }}>
                    {!u.profile_photo_url && (u.nickname?.[0] || "?").toUpperCase()}
                  </div>
                  <span style={{ fontWeight: 600 }}>{u.nickname}</span>
                  <span style={{ marginLeft: "auto", fontFamily: FL, fontSize: 10, color: C.primary, fontWeight: 700 }}>+ Add</span>
                </button>
              ))}
            </div>
          )}
          {searching && <p style={{ margin: "6px 0 0", fontFamily: FL, fontSize: 11, color: C.outlineVariant }}>{t("myplace.searching")}</p>}
        </div>
      )}
    </div>
  );
}
